/**
 * WallThumb — lightweight, non-interactive preview of a product on a wall.
 * Reuses the scenery's frame layering (frame webp over/under the photo) so the
 * catalog card matches the real product. Swap in a static AI room shot later by
 * passing `img` (rendered by the parent) instead of this live render.
 */
import React from "react";
import { frameByName, frameWebp, wallLayout, sceneById, wallGridWidthPctForAspect } from "../WallProduct/data";

// Per-product sample sets — a cohesive, on-theme AI set per product (square, warm,
// graded to the room). Keyed by product id; falls back to a couple of warm singles.
const set = (product, n) => Array.from({ length: n }, (_, i) => `/assets/shopping/samples/${product}/${String(i + 1).padStart(2, "0")}.webp`);
// One unique photo per cell (no repeated rows) — counts match each product's grid.
const SAMPLE_SETS = {
  "village-6": set("village-6", 6),
  "study-4": set("study-4", 4),
  "stone-16-diamond": set("stone-16-diamond", 16),
  "bedroom-calm-9": set("bedroom-calm-9", 9),
  "kids-blue-10": set("kids-blue-10", 10),
  "modern-styled-12": set("modern-styled-12", 12),
  "coastal-10": set("coastal-10", 10),
  // entry-24 wants 24 uniques but generation stopped at 15 (OpenAI billing hard limit);
  // reference the 15 that exist so the thumb cycles them (no 404s). Bump to 24 once the
  // remaining 16-24 are generated (frame_samples_gen.py already has all 24 prompts).
  "entry-24": set("entry-24", 15),
  "music-studio-14": set("music-studio-14", 14),
  "enterprise-lobby-40": set("enterprise-lobby-40", 40),
};
const FALLBACK_SAMPLES = ["/assets/images/cpl.jpg", "/assets/file/images/couple.png"];

export default function WallThumb({ frame = "classic", count = 6, cols, bw = false, scene = "living", rows = null, brick = null, samples, showRuler = false, dims = "" }) {
  const photos = SAMPLE_SETS[samples] || FALLBACK_SAMPLES;
  const meta = frameByName(frame);
  const flat = meta.shadow === false;
  const nCols = cols || wallLayout(count).cols;
  const hasRows = !!(rows && rows.length);            // horizontal diamond: one COLUMN per entry
  const hasBrick = !!(brick && brick.length);         // staggered brick: one ROW per entry, alt rows offset
  // Width driver: diamond = column count; brick = widest row; rectangular = nCols.
  const numCols = hasBrick ? Math.max(...brick) : hasRows ? rows.length : nCols;
  const numRows = hasBrick ? brick.length : hasRows ? Math.max(...rows) : Math.ceil(count / nCols);
  const g = sceneById(scene).grid;
  const style = {
    "--pt-cols": numCols,
    "--pt-sw": (meta.sw || 74) + "%",
    "--pt-frame": `url('${frameWebp(frame)}')`,
    backgroundImage: `url('/assets/shopping/env/${sceneById(scene).file}')`,
  };
  // Same REAL-WORLD sizing as the wall page (WALL_SCENE_PROPORTIONS.md). The thumb box
  // is 4:3 and cover-crops the 3:2 photo, so correct for that. Box height tracks
  // numRows/numCols so the tiles stay square; centre it in the scene's wall band.
  const gridW = wallGridWidthPctForAspect(numCols, scene, 4 / 3);
  // diamond columns carry a vertical margin-block:6% between frames -> box ~12% taller.
  const gridH = gridW * (4 / 3) * (numRows / numCols) * (hasRows ? 1.12 : 1);
  const side = (100 - gridW) / 2;
  const top = Math.max(3, g.top + g.height / 2 - gridH / 2);
  const gridStyle = { top: top + "%", height: gridH + "%", left: (side + g.cx) + "%", right: (side - g.cx) + "%" };
  // Dimension ruler: parse "W × H ..." -> width/height labels drawn over the grid rect.
  const dm = String(dims).match(/([\d.]+)\s*[×xX]\s*([\d.]+)/);
  const rw = dm ? dm[1] : null, rh = dm ? dm[2] : null;
  const cell = (i) => (
    <div className="pthumb-fr" key={i}>
      <div className="pthumb-photo" style={{ backgroundImage: `url('${photos[i % photos.length]}')` }} />
    </div>
  );
  return (
    <div className={`pthumb${flat ? " flat" : ""}${bw ? " bw" : ""}${hasBrick ? " brick" : hasRows ? " hdiamond" : ""}${showRuler ? " ruled" : ""}`} style={style}>
      <div className="pthumb-grid" style={gridStyle}>
        {hasBrick
          ? brick.map((n, r) => {
              const start = brick.slice(0, r).reduce((a, b) => a + b, 0);
              return <div className="pthumb-row" key={r}>{Array.from({ length: n }).map((_, k) => cell(start + k))}</div>;
            })
          : hasRows
          ? rows.map((n, c) => {
              const start = rows.slice(0, c).reduce((a, b) => a + b, 0);
              return <div className="pthumb-col" key={c}>{Array.from({ length: n }).map((_, k) => cell(start + k))}</div>;
            })
          : Array.from({ length: count }).map((_, i) => cell(i))}
      </div>
      {showRuler ? (
        <div className="pthumb-ruler" style={gridStyle}>
          <span className="pr-w" aria-hidden="true" />
          <span className="pr-h" aria-hidden="true" />
          {rw ? <span className="pr-wl"><b>{rw} ס״מ</b></span> : null}
          {rh ? <span className="pr-hl"><b>{rh} ס״מ</b></span> : null}
        </div>
      ) : null}
    </div>
  );
}
